import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_DELIVERY_POINTS_API_PATHS } from '../../delivery-points/constants/delivery-points-api-paths.constants';
import { DeliveryPointDetails } from '../../delivery-points/types/delivery-point-details.type';
import { isNotFoundError } from '../utils/is-not-found-error.util';

type UseLastOrderDeliveryPointResult = {
  isLoading: boolean;
  deliveryPoint: DeliveryPointDetails | null;
};

const INITIAL_STATE: UseLastOrderDeliveryPointResult = {
  isLoading: true,
  deliveryPoint: null,
};

export function useLastOrderDeliveryPoint({
  deliveryPointId,
}: {
  deliveryPointId: string;
}): UseLastOrderDeliveryPointResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState<UseLastOrderDeliveryPointResult>(INITIAL_STATE);

  useEffect(() => {
    let isCurrent = true;
    setResult(INITIAL_STATE);

    HttpClient.get<DeliveryPointDetails>(ADMIN_DELIVERY_POINTS_API_PATHS.GET_DELIVERY_POINT_BY_ID(deliveryPointId))
      .then((deliveryPoint) => {
        if (isCurrent) setResult({ isLoading: false, deliveryPoint: deliveryPoint ?? null });
      })
      .catch((error) => {
        if (!isCurrent) return;
        if (!isNotFoundError(error)) {
          enqueueSnackbar(
            extractTranslatedApiErrorMessage(
              error,
              'Une erreur est survenue lors de la récupération du point de livraison de la dernière commande'
            ),
            { variant: 'error' }
          );
        }
        setResult({ isLoading: false, deliveryPoint: null });
      });

    return () => {
      isCurrent = false;
    };
  }, [deliveryPointId, enqueueSnackbar]);

  return result;
}
